import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";
import { useLocalizedMeta } from "@/hooks/useLocalizedMeta";
import { getArticle, getArticleList } from "@/content/articles";
import { articleImages } from "@/content/articles/meta";
import { buildArticleSchema } from "@/data/schema";
import { MAIN_SITE_URL } from "@/data/site";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language ?? "en").split("-")[0];
  const article = slug ? getArticle(lang, slug) : undefined;

  useLocalizedMeta();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const related = getArticleList(lang)
    .filter((a) => a.slug !== slug)
    .slice(0, 3);

  const articleSchema = buildArticleSchema({
    title: article.title,
    description: article.description,
    slug: article.slug,
    image: articleImages[article.slug],
    lang,
  });

  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main>
        <article className="bg-background-50">
          <div className="mx-auto max-w-3xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
              <Link to="/" className="hover:text-primary-700 cursor-pointer transition-colors">
                {t("brand")}
              </Link>
              <span className="text-foreground-300">/</span>
              <span className="text-foreground-700">{t("nav.guides")}</span>
            </nav>

            <h1 className="mt-6 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {article.title}
            </h1>

            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {t("article.updated")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-global-line"></i>
                {t(`lang.${lang}`)}
              </span>
            </div>

            <div className="mt-8 space-y-4">
              {article.intro.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-foreground-700">
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-8 rounded-2xl border border-primary-200 bg-primary-100/60 p-6">
              <h2 className="font-heading text-lg font-bold text-primary-900">
                Key takeaways
              </h2>
              <ul className="mt-4 space-y-2.5">
                {article.takeaways.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-foreground-800">
                    <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {article.sections.map((section, i) => (
              <section key={i} className="mt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {section.heading}
                </h2>
                <div className="mt-4 space-y-4">
                  {section.paragraphs.map((p, j) => (
                    <p key={j} className="text-base leading-relaxed text-foreground-700">
                      {p}
                    </p>
                  ))}
                </div>
                {section.items && (
                  <ul className="mt-4 space-y-2.5">
                    {section.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm text-foreground-800">
                        <i className="ri-check-line mt-0.5 text-primary-600"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            <section className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                {t("faq.title")}
              </h2>
              <div className="mt-4 space-y-3">
                {article.faq.map((item, i) => (
                  <div key={i} className="rounded-xl border border-background-200 bg-background-50 p-5">
                    <h3 className="text-sm font-semibold text-foreground-900">
                      {item.q}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                      {item.a}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <p className="mt-10 text-base leading-relaxed text-foreground-700">
              {article.conclusion}
            </p>

            {related.length > 0 && (
              <section className="mt-12">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  {t("article.related")}
                </h2>
                <div className="mt-4 space-y-3">
                  {related.map((r) => (
                    <Link
                      key={r.slug}
                      to={`/article/${r.slug}`}
                      className="flex items-center gap-3 rounded-xl border border-background-200 bg-background-50 p-4 hover:border-primary-300 cursor-pointer transition-colors"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                        <i className="ri-file-text-line"></i>
                      </span>
                      <span className="text-sm font-medium text-foreground-800 hover:text-primary-700">
                        {r.title}
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-10 flex flex-col gap-4 rounded-2xl border border-background-200 bg-background-100 p-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-foreground-500">
                  {t("ctaSection.subtitle")}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-50 cursor-pointer transition-colors"
                >
                  <i className="ri-home-4-line"></i>
                  {t("brand")}
                </Link>
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer transition-colors"
                >
                  {t("cta.visitMain")}
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}